import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent, FormEvent, MouseEvent } from 'react';

import {
  api,
  type OptionsSnapshot,
  type TickerSearchResult,
  type PriceHistoryPoint,
  type OptionsContract
} from '../../../api';
import type { FeatureContext } from '../../types';
import './options-trading.css';

interface LadderRow {
  strike: number;
  call: TickerOption | null;
  put: TickerOption | null;
}

type TickerOption = OptionsSnapshot['chain']['calls'][number];

type HistoryPeriod = '1d' | 'ytd' | '1y' | '3y' | '5y' | 'max';

const HISTORY_PERIOD_OPTIONS: Array<{ value: HistoryPeriod; label: string }> = [
  { value: '1d', label: '1D' },
  { value: 'ytd', label: 'YTD' },
  { value: '1y', label: '1Y' },
  { value: '3y', label: '3Y' },
  { value: '5y', label: '5Y' },
  { value: 'max', label: 'MAX' }
];

export function OptionsTradingFeature({ isAuthenticated }: FeatureContext) {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TickerSearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<OptionsSnapshot | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [historyPeriod, setHistoryPeriod] = useState<HistoryPeriod>('1y');
  const [history, setHistory] = useState<PriceHistoryPoint[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [hoveredHistoryIndex, setHoveredHistoryIndex] = useState<number | null>(null);
  const [hoveredMaxPainIndex, setHoveredMaxPainIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setSearchResults([]);
      setSearchLoading(false);
      setShowSearchResults(false);
      setHistory([]);
      setHistoryError(null);
      setHoveredHistoryIndex(null);
      setHoveredMaxPainIndex(null);
      return;
    }

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError(null);
      setShowSearchResults(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    setSearchError(null);

    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const results = await api.searchTickers(trimmed);
          if (!cancelled) {
            setSearchResults(results);
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Ticker search failed.';
          if (!cancelled) {
            setSearchError(message);
            setSearchResults([]);
          }
        } finally {
          if (!cancelled) {
            setSearchLoading(false);
          }
        }
      })();
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query, isAuthenticated]);

  const loadHistory = useCallback(
    async (symbol: string, periodValue: HistoryPeriod) => {
      if (!isAuthenticated) {
        setHistoryError('Sign in to view historical pricing.');
        return;
      }

      const normalized = symbol.trim();
      if (!normalized) {
        return;
      }

      setHistoryLoading(true);
      setHistoryError(null);

      try {
        const response = await api.getPriceHistory({ ticker: normalized, period: periodValue });
        setHistory(response.points);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load price history.';
        setHistoryError(message);
        setHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    },
    [isAuthenticated]
  );

  const loadSnapshot = useCallback(
    async (symbol: string, expiration?: number) => {
      if (!isAuthenticated) {
        setSnapshotError('Sign in to load live market data.');
        return;
      }

      const normalized = symbol.trim();
      if (!normalized) {
        return;
      }

      setSnapshotLoading(true);
      setSnapshotError(null);
      setShowSearchResults(false);

      try {
        const response = await api.getOptionsSnapshot({ ticker: normalized, expiration });
        setSnapshot(response);
        void loadHistory(normalized, historyPeriod);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load quote and options data.';
        setSnapshotError(message);
        setSnapshot(null);
      } finally {
        setSnapshotLoading(false);
      }
    },
    [isAuthenticated, loadHistory, historyPeriod]
  );

  const handleSearchSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!query.trim()) {
        return;
      }
      setSearchResults([]);
      setShowSearchResults(false);
      void loadSnapshot(query.trim());
    },
    [loadSnapshot, query]
  );

  const handleResultSelect = useCallback(
    (result: TickerSearchResult) => {
      setQuery(result.symbol);
      setSearchResults([]);
      setShowSearchResults(false);
      void loadSnapshot(result.symbol);
    },
    [loadSnapshot]
  );

  useEffect(() => {
    if (snapshot?.ticker) {
      setHoveredHistoryIndex(null);
      setHoveredMaxPainIndex(null);
      void loadHistory(snapshot.ticker, historyPeriod);
    }
  }, [snapshot?.ticker, historyPeriod, loadHistory]);

  const handleQueryChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setShowSearchResults(true);
  }, []);

  const handleExpirationChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const value = Number(event.target.value);
      if (!snapshot) {
        return;
      }
      void loadSnapshot(snapshot.ticker, Number.isFinite(value) ? value : undefined);
    },
    [loadSnapshot, snapshot]
  );

  const ladderRows = useMemo(() => {
    if (!snapshot) {
      return [];
    }

    const callMap = new Map(snapshot.chain.calls.map((contract) => [contract.strike, contract]));
    const putMap = new Map(snapshot.chain.puts.map((contract) => [contract.strike, contract]));
    const uniqueStrikes = Array.from(new Set([...callMap.keys(), ...putMap.keys()])).sort(
      (a, b) => a - b
    );

    const targetPrice = snapshot.quote.regularMarketPrice ?? uniqueStrikes[Math.floor(uniqueStrikes.length / 2)] ?? 0;
    const focusIndex = uniqueStrikes.findIndex((strike) => strike >= targetPrice);
    const start = Math.max(0, focusIndex === -1 ? uniqueStrikes.length - 15 : focusIndex - 8);
    const end = Math.min(uniqueStrikes.length, start + 16);
    const windowed = uniqueStrikes.slice(start, end);

    return windowed.map<LadderRow>((strike) => ({
      strike,
      call: callMap.get(strike) ?? null,
      put: putMap.get(strike) ?? null
    }));
  }, [snapshot]);

  const currency = snapshot?.quote.currency ?? 'USD';
  const expirationOptions = snapshot?.chain.availableExpirations ?? [];
  const selectedExpirationLabel = snapshot ? formatExpiration(snapshot.chain.selectedExpiration) : null;
  const externalMaxPain = snapshot?.maxPain && snapshot.maxPain.strike > 10 ? snapshot.maxPain : null;

  const maxPainSeriesData = useMemo(() => {
    if (!snapshot) {
      return [];
    }

    const backendSeries = snapshot.chain.maxPainSeries ?? [];
    const sanitized = backendSeries
      .map((point) => ({
        expiration: point.expiration,
        strike: point.strike,
        date: new Date(point.expiration * 1000)
      }))
      .filter(
        (point): point is { expiration: number; strike: number; date: Date } =>
          typeof point.strike === 'number' && Number.isFinite(point.strike)
      )
      .sort((a, b) => a.expiration - b.expiration);

    if (sanitized.length > 0) {
      return sanitized;
    }

    const fallback = calculateSelectedMaxPain(snapshot.chain.calls, snapshot.chain.puts);
    if (fallback !== null) {
      return [
        {
          expiration: snapshot.chain.selectedExpiration,
          strike: fallback,
          date: new Date(snapshot.chain.selectedExpiration * 1000)
        }
      ];
    }

    return [];
  }, [snapshot]);

  const derivedMaxPainStrike = useMemo(() => {
    if (!snapshot) {
      return null;
    }

    const match = maxPainSeriesData.find(
      (point) => point.expiration === snapshot.chain.selectedExpiration
    );
    if (match) {
      return match.strike;
    }

    return calculateSelectedMaxPain(snapshot.chain.calls, snapshot.chain.puts);
  }, [snapshot, maxPainSeriesData]);

  const chartWidth = 680;
  const chartHeight = 260;
  const chartPaddingLeft = 48;
  const chartPaddingRight = 20;
  const chartAxisOffset = 32;
  const maxPainChartHeight = 220;
  const maxPainChartPaddingLeft = 60;
  const maxPainChartPaddingRight = 16;
  const maxPainAxisOffset = 32;

  const historyChart = useMemo<{
    linePath: string;
    areaPath: string;
    samples: Array<{ x: number; y: number; value: number; date: Date }>;
    startLabel: Date;
    endLabel: Date;
    yTicks: Array<{ y: number; value: number }>;
    xTicks: Array<{ x: number; date: Date; rotate?: boolean }>;
  } | null>(() => {
    const usable = history.filter((point) => point.close !== null);
    if (usable.length < 2) {
      return null;
    }

    const parsed = usable.map((point) => ({
      x: new Date(point.timestamp).getTime(),
      y: point.close as number
    }));

    const minX = Math.min(...parsed.map((point) => point.x));
    const maxX = Math.max(...parsed.map((point) => point.x));
    const minY = Math.min(...parsed.map((point) => point.y));
    const maxY = Math.max(...parsed.map((point) => point.y));

    const spanX = maxX - minX || 1;
    const spanY = maxY - minY || 1;

    const usableWidth = Math.max(chartWidth - chartPaddingLeft - chartPaddingRight, 1);
    const coords = parsed.map((point) => {
      const relativeX = (point.x - minX) / spanX;
      const relativeY = (point.y - minY) / spanY;
      return {
        x: chartPaddingLeft + relativeX * usableWidth,
        y: chartHeight - relativeY * chartHeight
      };
    });

    const linePath = coords
      .map((coord, index) => `${index === 0 ? 'M' : 'L'}${coord.x.toFixed(2)} ${coord.y.toFixed(2)}`)
      .join(' ');

    const first = coords[0];
    const last = coords[coords.length - 1];
    const areaPath = `${linePath} L${last.x.toFixed(2)} ${chartHeight} L${first.x.toFixed(2)} ${chartHeight} Z`;

    const samples = coords.map((coord, index) => ({
      x: coord.x,
      y: coord.y,
      value: parsed[index].y,
      date: new Date(usable[index].timestamp)
    }));

    const tickValues = generateValueTicks(minY, maxY);
    const yTicks = tickValues.map((value) => ({
      value,
      y: chartHeight - ((value - minY) / spanY) * chartHeight
    }));

    const xTicks = buildDateTicks(
      samples.map((sample) => sample.date),
      coords,
      6,
      false,
      chartPaddingLeft + 6,
      chartPaddingRight + 6
    );

    return {
      linePath,
      areaPath,
      samples,
      startLabel: samples[0].date,
      endLabel: samples[samples.length - 1].date,
      yTicks,
      xTicks
    };
  }, [history, chartHeight, chartWidth]);

  const handleHistoryPeriodChange = useCallback((value: HistoryPeriod) => {
    setHistoryPeriod(value);
  }, []);

  const activeHistoryPoint = useMemo(() => {
    if (!historyChart) {
      return null;
    }
    if (hoveredHistoryIndex !== null) {
      return historyChart.samples[hoveredHistoryIndex] ?? null;
    }
    return historyChart.samples[historyChart.samples.length - 1] ?? null;
  }, [historyChart, hoveredHistoryIndex]);

  const handleChartPointerMove = useCallback(
    (event: MouseEvent<SVGSVGElement>) => {
      if (!historyChart) {
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const relativeX = ((event.clientX - rect.left) / rect.width) * chartWidth;

      let closestIndex = 0;
      let smallestDistance = Infinity;
      historyChart.samples.forEach((sample, index) => {
        const distance = Math.abs(sample.x - relativeX);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          closestIndex = index;
        }
      });

      setHoveredHistoryIndex(closestIndex);
    },
    [historyChart, chartWidth]
  );

  const handleChartPointerLeave = useCallback(() => {
    setHoveredHistoryIndex(null);
  }, []);

const maxPainChart = useMemo<{
  linePath: string;
  areaPath: string;
  points: Array<{ x: number; y: number; strike: number; date: Date; expiration: number }>;
  yTicks: Array<{ y: number; value: number }>;
  xTicks: Array<{ x: number; date: Date; rotate?: boolean }>;
} | null>(() => {
  if (maxPainSeriesData.length === 0) {
    return null;
  }

    const dataset =
      maxPainSeriesData.length === 1
        ? [...maxPainSeriesData, maxPainSeriesData[0]]
        : maxPainSeriesData;

    const strikes = dataset.map((point) => point.strike);
    const minRaw = Math.min(...strikes);
    const maxRaw = Math.max(...strikes);
    const padding = Math.max((maxRaw - minRaw) * 0.1, 5);
    const minY = minRaw - padding;
    const maxY = maxRaw + padding;
    const spanY = maxY - minY || 1;

    const minX = dataset[0].date.getTime();
    const maxX = dataset[dataset.length - 1].date.getTime();
    const spanX = maxX - minX || 1;

    const usableWidth = Math.max(chartWidth - maxPainChartPaddingLeft - maxPainChartPaddingRight, 1);
    const points = dataset.map((point) => {
      const relX = (point.date.getTime() - minX) / spanX;
      const relY = (point.strike - minY) / spanY;
      return {
        x: maxPainChartPaddingLeft + relX * usableWidth,
        y: maxPainChartHeight - relY * maxPainChartHeight,
        strike: point.strike,
        date: point.date,
        expiration: point.expiration
      };
    });

    const linePath = points
      .map((coord, index) => `${index === 0 ? 'M' : 'L'}${coord.x.toFixed(2)} ${coord.y.toFixed(2)}`)
      .join(' ');

    const last = points[points.length - 1];
    const first = points[0];
    const areaPath = `${linePath} L${last.x.toFixed(2)} ${maxPainChartHeight} L${first.x.toFixed(2)} ${maxPainChartHeight} Z`;

    const yTicks = generateValueTicks(minY, maxY).map((value) => ({
      value,
      y: maxPainChartHeight - ((value - minY) / spanY) * maxPainChartHeight
    }));

    const desiredTickCount = Math.min(16, Math.max(8, dataset.length));
    const xTicks = buildDateTicks(
      points.map((sample) => sample.date),
      points,
      desiredTickCount,
      true,
      maxPainChartPaddingLeft + 6,
      maxPainChartPaddingRight + 6
    );

    return { linePath, areaPath, points, yTicks, xTicks };
  }, [chartWidth, maxPainChartHeight, maxPainSeriesData]);

  const activeMaxPainPoint = useMemo(() => {
    if (!maxPainChart) {
      return null;
    }
    if (hoveredMaxPainIndex !== null) {
      return maxPainChart.points[hoveredMaxPainIndex] ?? null;
    }
    return maxPainChart.points[maxPainChart.points.length - 1];
  }, [maxPainChart, hoveredMaxPainIndex]);

  const applyMaxPainSelection = useCallback(
    (index: number) => {
      if (!maxPainChart || !snapshot) {
        return;
      }

      const point = maxPainChart.points[index];
      const expirationSeconds = Math.trunc(point.expiration);

      setHoveredMaxPainIndex(index);
      setSnapshot((previous) => {
        if (!previous) {
          return previous;
        }

        return {
          ...previous,
          chain: {
            ...previous.chain,
            selectedExpiration: expirationSeconds
          }
        };
      });

      if (snapshot.chain.selectedExpiration !== expirationSeconds) {
        void loadSnapshot(snapshot.ticker, expirationSeconds);
      }
    },
    [maxPainChart, snapshot, loadSnapshot]
  );

  const handleMaxPainPointerMove = useCallback(
    (event: MouseEvent<SVGSVGElement>) => {
      if (!maxPainChart) {
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const relativeX = ((event.clientX - rect.left) / rect.width) * chartWidth;

      let closestIndex = 0;
      let smallestDistance = Infinity;
      maxPainChart.points.forEach((point, index) => {
        const distance = Math.abs(point.x - relativeX);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          closestIndex = index;
        }
      });

      setHoveredMaxPainIndex(closestIndex);
    },
    [maxPainChart, chartWidth]
  );

  const handleMaxPainPointerLeave = useCallback(() => {
    setHoveredMaxPainIndex(null);
  }, []);

  const handleMaxPainChartClick = useCallback(
    (event: MouseEvent<SVGSVGElement>) => {
      if (!maxPainChart) {
        return;
      }

      const svgRect = event.currentTarget.getBoundingClientRect();
      const scaleX = chartWidth / svgRect.width;
      const pointerX = (event.clientX - svgRect.left) * scaleX;

      let closestIndex = 0;
      let smallestDistance = Infinity;
      maxPainChart.points.forEach((point, index) => {
        const distance = Math.abs(point.x - pointerX);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          closestIndex = index;
        }
      });

      applyMaxPainSelection(closestIndex);
    },
    [maxPainChart, chartWidth, applyMaxPainSelection]
  );

  return (
    <section className="options-trading">
      <header className="options-trading__header">
        <p className="options-trading__pill">Finance</p>
        <h1>Options Trading</h1>
        <p className="options-trading__lede">
          Search for an equity ticker to pull its latest quote, visualize the near-term options ladder, and surface the
          max pain strike before committing to a strategy.
        </p>
      </header>

      {!isAuthenticated && (
        <p className="options-trading__notice">
          This feature needs an authenticated session so we can authorize outbound market-data requests routed through the
          backend API.
        </p>
      )}

      <form className="options-trading__search" onSubmit={handleSearchSubmit}>
        <label htmlFor="options-trading-search" className="options-trading__search-label">
          Company or ticker
        </label>
        <div className="options-trading__search-input">
          <input
            id="options-trading-search"
            type="search"
            value={query}
            disabled={!isAuthenticated}
            onChange={handleQueryChange}
            placeholder="e.g. MSFT, NVDA, Tesla"
          />
          <button type="submit" disabled={!isAuthenticated || !query.trim()}>
            Load data
          </button>
        </div>
        <div className="options-trading__search-status">
          {searchLoading && <span>Searching…</span>}
          {searchError && <span className="options-trading__error">{searchError}</span>}
          {!searchLoading && !searchError && query.trim().length > 0 && searchResults.length === 0 && (
            <span>Enter at least two characters or type an exact ticker then press Enter.</span>
          )}
        </div>
        {showSearchResults && searchResults.length > 0 && (
          <ul className="options-trading__search-results">
            {searchResults.map((result) => (
              <li key={result.symbol}>
                <button type="button" onClick={() => handleResultSelect(result)}>
                  <span className="options-trading__search-symbol">{result.symbol}</span>
                  <span className="options-trading__search-name">
                    {result.shortName || result.longName || 'Unknown security'}
                  </span>
                  {result.exchange && <span className="options-trading__search-exchange">{result.exchange}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </form>

      <div className="options-trading__grid">
        <div className="options-trading__card options-trading__card--quote">
          <div className="options-trading__card-header">
            <h2>Equity snapshot</h2>
            {snapshot && (
              <p className="options-trading__ticker">
                {snapshot.quote.symbol}{' '}
                <span>{snapshot.quote.shortName || snapshot.quote.longName || 'Unnamed security'}</span>
              </p>
            )}
          </div>
          {!snapshot && !snapshotLoading && (
            <p className="options-trading__placeholder">Use the search above to load a ticker.</p>
          )}
          {snapshotLoading && <p>Loading the latest quote and options chain…</p>}
          {snapshotError && <p className="options-trading__error">{snapshotError}</p>}
          {snapshot && !snapshotLoading && (
            <div className="options-trading__quote">
              <div>
                <p className="options-trading__price">{formatCurrency(snapshot.quote.regularMarketPrice, currency)}</p>
                <p
                  className={
                    (snapshot.quote.regularMarketChange ?? 0) >= 0
                      ? 'options-trading__change options-trading__change--positive'
                      : 'options-trading__change options-trading__change--negative'
                  }
                >
                  {formatSigned(snapshot.quote.regularMarketChange, currency)} (
                  {formatPercent(snapshot.quote.regularMarketChangePercent)})
                </p>
              </div>
              <dl>
                <div>
                  <dt>Previous close</dt>
                  <dd>{formatCurrency(snapshot.quote.regularMarketPreviousClose, currency)}</dd>
                </div>
                <div>
                  <dt>Day range</dt>
                  <dd>
                    {formatCurrency(snapshot.quote.regularMarketDayLow, currency)} –{' '}
                    {formatCurrency(snapshot.quote.regularMarketDayHigh, currency)}
                  </dd>
                </div>
                <div>
                  <dt>52-week range</dt>
                  <dd>
                    {formatCurrency(snapshot.quote.fiftyTwoWeekLow, currency)} –{' '}
                    {formatCurrency(snapshot.quote.fiftyTwoWeekHigh, currency)}
                  </dd>
                </div>
                <div>
                  <dt>Market cap</dt>
                  <dd>{formatCompact(snapshot.quote.marketCap)}</dd>
                </div>
                <div>
                  <dt>Volume</dt>
                  <dd>{formatNumber(snapshot.quote.regularMarketVolume)}</dd>
                </div>
              </dl>
            </div>
          )}
        </div>

      </div>

      {snapshot && (
        <div className="options-trading__card options-trading__card--chart">
          <div className="options-trading__card-header">
            <h2>Price history</h2>
            <div className="options-trading__period-toggle">
              {HISTORY_PERIOD_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={
                    option.value === historyPeriod
                      ? 'options-trading__period-button options-trading__period-button--active'
                      : 'options-trading__period-button'
                  }
                  onClick={() => handleHistoryPeriodChange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          {historyLoading ? (
            <p>Loading historical prices…</p>
          ) : historyError ? (
            <p className="options-trading__error">{historyError}</p>
          ) : historyChart ? (
            <>
              <svg
                className="options-trading__chart"
                viewBox={`0 0 ${chartWidth} ${chartHeight + chartAxisOffset}`}
                role="img"
                aria-label="Historical price chart"
                onMouseMove={handleChartPointerMove}
                onMouseLeave={handleChartPointerLeave}
              >
                <defs>
                  <linearGradient id="options-history-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="rgba(63, 138, 255, 0.45)" />
                    <stop offset="100%" stopColor="rgba(63, 138, 255, 0.12)" />
                  </linearGradient>
                </defs>
                <line
                  x1={chartPaddingLeft}
                  y1={chartHeight}
                  x2={chartWidth - chartPaddingRight}
                  y2={chartHeight}
                  className="options-trading__chart-axis"
                />
                <line
                  x1={chartPaddingLeft}
                  y1={0}
                  x2={chartPaddingLeft}
                  y2={chartHeight}
                  className="options-trading__chart-axis"
                />
                <path
                  d={historyChart.areaPath}
                  fill="url(#options-history-gradient)"
                  stroke="none"
                  aria-hidden="true"
                />
                <path
                  d={historyChart.linePath}
                  fill="none"
                  stroke="var(--color-accent)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {activeHistoryPoint && (
                  <>
                    <line
                      x1={activeHistoryPoint.x}
                      x2={activeHistoryPoint.x}
                      y1={0}
                      y2={chartHeight}
                      stroke="rgba(63, 138, 255, 0.4)"
                      strokeDasharray="4"
                    />
                    <circle
                      cx={activeHistoryPoint.x}
                      cy={activeHistoryPoint.y}
                      r="5"
                      fill="#fff"
                      stroke="var(--color-accent)"
                      strokeWidth="2"
                    />
                  </>
                )}
                {historyChart.yTicks.map((tick, index) => (
                  <g key={`y-${index}`}>
                    <line
                      x1={chartPaddingLeft}
                      x2={chartWidth - chartPaddingRight}
                      y1={tick.y}
                      y2={tick.y}
                      className="options-trading__chart-grid"
                    />
                    <text
                      x={chartPaddingLeft - 10}
                      y={tick.y - 2}
                      textAnchor="end"
                      alignmentBaseline="middle"
                      className="options-trading__chart-axis-label"
                    >
                      {formatAxisPrice(tick.value, currency)}
                    </text>
                  </g>
                ))}
                {historyChart.xTicks.map((tick, index) => (
                  <text
                    key={`x-${index}`}
                    x={tick.x}
                    y={chartHeight + chartAxisOffset - 6}
                    textAnchor="middle"
                    className={
                      tick.rotate
                        ? 'options-trading__chart-axis-label options-trading__chart-axis-label--x'
                        : 'options-trading__chart-axis-label'
                    }
                  >
                    {formatHistoryLabel(tick.date, historyPeriod === '1d')}
                  </text>
                ))}
              </svg>
              <div className="options-trading__chart-meta">
                <div className="options-trading__chart-range">
                  <p>
                    {historyChart.startLabel
                      ? formatHistoryLabel(historyChart.startLabel, historyPeriod === '1d')
                      : ''}
                  </p>
                </div>
                <div className="options-trading__chart-value">
                  <p>{formatCurrency(activeHistoryPoint?.value ?? null, currency)}</p>
                  <span>
                    {activeHistoryPoint?.date ? formatHistoryLabel(activeHistoryPoint.date, historyPeriod === '1d') : ''}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <p className="options-trading__placeholder">No historical data available for this period.</p>
          )}
        </div>
      )}

      {maxPainChart && (
        <div className="options-trading__card options-trading__card--experiment">
          <div className="options-trading__card-header">
            <h2>Experimental · Max pain trajectory</h2>
            <p className="options-trading__experiment-label">Aggregation across all available expirations</p>
          </div>
          <svg
            className="options-trading__chart"
            viewBox={`0 0 ${chartWidth} ${maxPainChartHeight + maxPainAxisOffset}`}
            role="img"
            aria-label="Max pain by expiration"
            onMouseMove={handleMaxPainPointerMove}
            onMouseLeave={handleMaxPainPointerLeave}
            onClick={handleMaxPainChartClick}
          >
            <defs>
              <linearGradient id="options-maxpain-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(255, 158, 38, 0.35)" />
                <stop offset="100%" stopColor="rgba(255, 158, 38, 0.08)" />
              </linearGradient>
            </defs>
            <line
              x1={maxPainChartPaddingLeft}
              y1={maxPainChartHeight}
              x2={chartWidth - maxPainChartPaddingRight}
              y2={maxPainChartHeight}
              className="options-trading__chart-axis"
            />
            <line
              x1={maxPainChartPaddingLeft}
              y1={0}
              x2={maxPainChartPaddingLeft}
              y2={maxPainChartHeight}
              className="options-trading__chart-axis"
            />
            <path
              d={maxPainChart.areaPath}
              fill="url(#options-maxpain-gradient)"
              stroke="none"
              aria-hidden="true"
            />
            <path
              d={maxPainChart.linePath}
              fill="none"
              stroke="#ff9e26"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {maxPainChart.points.map((point, index) => {
              const isSelected = snapshot && point.expiration === snapshot.chain.selectedExpiration;
              const isActive = activeMaxPainPoint && point.expiration === activeMaxPainPoint.expiration;
              const isFilled = isSelected || isActive;

              return (
                <circle
                  key={`mp-${index}`}
                  cx={point.x}
                  cy={point.y}
                  r={isFilled ? 6 : 4}
                  fill={isFilled ? '#ff9e26' : '#fff'}
                  stroke="#ff9e26"
                  strokeWidth={isFilled ? 3 : 2}
                />
              );
            })}
            {activeMaxPainPoint && (
              <>
                <line
                  x1={activeMaxPainPoint.x}
                  x2={activeMaxPainPoint.x}
                  y1={0}
                  y2={maxPainChartHeight}
                  stroke="rgba(255, 158, 38, 0.35)"
                  strokeDasharray="4"
                />
                <circle
                  cx={activeMaxPainPoint.x}
                  cy={activeMaxPainPoint.y}
                  r={6}
                  fill="#fff"
                  stroke="#ff9e26"
                  strokeWidth={3}
                />
              </>
            )}
            {maxPainChart.yTicks.map((tick, index) => (
              <text
                key={`mp-y-${index}`}
                x={maxPainChartPaddingLeft - 10}
                y={tick.y - 2}
                textAnchor="end"
                alignmentBaseline="middle"
                className="options-trading__chart-axis-label"
              >
                {formatAxisPrice(tick.value, currency)}
              </text>
            ))}
            {maxPainChart.xTicks.map((tick, index) => (
              <text
                key={`mp-x-${index}`}
                x={tick.x}
                y={maxPainChartHeight + maxPainAxisOffset - 10}
                textAnchor="middle"
                alignmentBaseline="hanging"
                className={
                  tick.rotate
                    ? 'options-trading__chart-axis-label options-trading__chart-axis-label--x'
                    : 'options-trading__chart-axis-label'
                }
                transform={
                  tick.rotate
                    ? `rotate(-55 ${tick.x} ${maxPainChartHeight + maxPainAxisOffset - 10})`
                    : undefined
                }
              >
                {formatExpirationDate(tick.date)}
              </text>
            ))}
          </svg>
          {activeMaxPainPoint && (
            <div className="options-trading__chart-meta">
              <div>
                <p>{formatExpirationDate(activeMaxPainPoint.date)}</p>
              </div>
              <div className="options-trading__chart-value">
                <p>{formatCurrency(activeMaxPainPoint.strike, currency)}</p>
                <span>Max pain</span>
              </div>
            </div>
          )}
        </div>
      )}

      {snapshot && (
        <div className="options-trading__card options-trading__card--ladder">
          <div className="options-trading__card-header options-trading__card-header--ladder">
            <h2>Options ladder</h2>
            {expirationOptions.length > 1 && (
              <label className="options-trading__expiration">
                Expiration
                <select value={snapshot.chain.selectedExpiration} onChange={handleExpirationChange}>
                  {expirationOptions.map((value) => (
                    <option key={value} value={value}>
                      {formatExpiration(value)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {expirationOptions.length === 1 && (
              <p className="options-trading__expiration-label">Expiration: {formatExpiration(expirationOptions[0])}</p>
            )}
          </div>

          <div className="options-trading__maxpain">
            {snapshotLoading ? (
              <p>Resolving latest cross-market positioning…</p>
            ) : snapshot && (derivedMaxPainStrike !== null || externalMaxPain) ? (
              <div className="options-trading__maxpain-grid">
                {derivedMaxPainStrike !== null && (
                  <div>
                    <p className="options-trading__metric-label">
                      Selected expiration{selectedExpirationLabel ? ` • ${selectedExpirationLabel}` : ''}
                    </p>
                    <p className="options-trading__metric-value">
                      {formatCurrency(derivedMaxPainStrike, currency)}
                    </p>
                    <p className="options-trading__metric-detail">
                      Calculated from current call/put open interest totals.
                    </p>
                  </div>
                )}
                {externalMaxPain && (
                  <div>
                    <p className="options-trading__metric-label">maximum-pain.com</p>
                    <p className="options-trading__metric-value">
                      {formatCurrency(externalMaxPain.strike, currency)}
                    </p>
                    <p className="options-trading__metric-detail">
                      Last updated {formatTimestamp(externalMaxPain.asOf)}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="options-trading__placeholder">
                Max pain is fetched from maximum-pain.com after you load a ticker.
              </p>
            )}
          </div>

          {ladderRows.length === 0 ? (
            <p className="options-trading__placeholder">No option contracts available for this expiration.</p>
          ) : (
            <div className="options-trading__table-wrapper">
              <table className="options-trading__table">
                <thead>
                  <tr>
                    <th colSpan={4}>Calls</th>
                    <th rowSpan={2}>Strike</th>
                    <th colSpan={4}>Puts</th>
                  </tr>
                  <tr>
                    <th>Bid</th>
                    <th>Ask</th>
                    <th>OI</th>
                    <th>Vol</th>
                    <th>Bid</th>
                    <th>Ask</th>
                    <th>OI</th>
                    <th>Vol</th>
                  </tr>
                </thead>
                <tbody>
                  {ladderRows.map((row) => (
                    <tr key={`${row.strike}`}>
                      <td>{formatCurrency(row.call?.bid ?? null, currency)}</td>
                      <td>{formatCurrency(row.call?.ask ?? null, currency)}</td>
                      <td>{formatNumber(row.call?.openInterest)}</td>
                      <td>{formatNumber(row.call?.volume)}</td>
                      <td>{formatCurrency(row.strike, currency)}</td>
                      <td>{formatCurrency(row.put?.bid ?? null, currency)}</td>
                      <td>{formatCurrency(row.put?.ask ?? null, currency)}</td>
                      <td>{formatNumber(row.put?.openInterest)}</td>
                      <td>{formatNumber(row.put?.volume)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </section>
  );
}

function formatCurrency(value: number | null | undefined, currency = 'USD'): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
  } catch {
    return value.toFixed(2);
  }
}

function formatCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(value);
}

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  return new Intl.NumberFormat('en-US').format(value);
}

function formatSigned(value: number | null | undefined, currency = 'USD'): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  if (Math.abs(value) < Number.EPSILON) {
    return formatCurrency(0, currency);
  }

  const formatted = formatCurrency(Math.abs(value), currency);
  return value > 0 ? `+${formatted}` : `-${formatted}`;
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function formatTimestamp(value: string | null): string {
  if (!value) {
    return 'unknown time';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'unknown time';
  }

  return date.toLocaleString();
}

function formatExpiration(timestamp: number): string {
  if (!Number.isFinite(timestamp)) {
    return 'Invalid date';
  }
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatHistoryDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatHistoryLabel(date: Date, includeTime: boolean): string {
  if (includeTime) {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  return formatHistoryDate(date);
}

function formatAxisPrice(value: number, currency = 'USD'): string {
  if (!Number.isFinite(value)) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2
  }).format(value);
}

function formatExpirationDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function calculateSelectedMaxPain(
  calls: OptionsContract[],
  puts: OptionsContract[]
): number | null {
  const totals = new Map<number, number>();

  for (const contract of calls) {
    if (typeof contract.strike !== 'number') {
      continue;
    }
    const current = totals.get(contract.strike) ?? 0;
    totals.set(contract.strike, current + (contract.openInterest ?? 0));
  }

  for (const contract of puts) {
    if (typeof contract.strike !== 'number') {
      continue;
    }
    const current = totals.get(contract.strike) ?? 0;
    totals.set(contract.strike, current + (contract.openInterest ?? 0));
  }

  if (totals.size === 0) {
    return null;
  }

  let bestStrike: number | null = null;
  let bestValue = -Infinity;
  for (const [strike, value] of totals.entries()) {
    if (value > bestValue) {
      bestStrike = strike;
      bestValue = value;
    }
  }

  return bestStrike;
}

function generateValueTicks(min: number, max: number, desired = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [];
  }

  if (min === max) {
    return [min];
  }

  const range = max - min;
  const roughStep = range / desired;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const steps = [1, 2, 2.5, 5, 10];
  const niceStep = steps
    .map((step) => step * magnitude)
    .find((step) => step >= roughStep) ?? roughStep;

  const firstTick = Math.floor(min / niceStep) * niceStep;
  const ticks: number[] = [];
  for (let value = firstTick; value <= max + niceStep; value += niceStep) {
    if (value >= min - niceStep * 0.5 && value <= max + niceStep * 0.5) {
      ticks.push(value);
    }
  }

  return ticks;
}

function clampAxisLabelX(
  value: number,
  coords: Array<{ x: number }>,
  paddingLeft = 40,
  paddingRight = paddingLeft
): number {
  if (coords.length === 0) {
    return value;
  }

  const minX = Math.min(...coords.map((coord) => coord.x));
  const maxX = Math.max(...coords.map((coord) => coord.x));
  if (maxX - minX <= paddingLeft + paddingRight) {
    return value;
  }
  return Math.min(Math.max(value, minX + paddingLeft), maxX - paddingRight);
}

function buildDateTicks(
  dates: Date[],
  coords: Array<{ x: number }>,
  desired = 4,
  rotate = false,
  paddingLeftOverride?: number,
  paddingRightOverride?: number
): Array<{ x: number; date: Date; rotate?: boolean }> {
  if (dates.length === 0) {
    return [];
  }

  const normalizedDesired = Math.max(2, Math.min(desired, dates.length));

  if (dates.length <= normalizedDesired) {
    return dates.map((date, index) => ({
      x: clampAxisLabelX(
        coords[index].x,
        coords,
        paddingLeftOverride ?? 40,
        paddingRightOverride ?? paddingLeftOverride ?? 40
      ),
      date,
      rotate
    }));
  }

  const ticks: Array<{ x: number; date: Date; rotate?: boolean }> = [];
  const step = Math.max(1, Math.round((dates.length - 1) / (normalizedDesired - 1)));

  for (let index = 0; index < dates.length; index += step) {
    const clampedIndex = Math.min(index, dates.length - 1, coords.length - 1);
    ticks.push({
      x: clampAxisLabelX(
        coords[clampedIndex].x,
        coords,
        paddingLeftOverride ?? 40,
        paddingRightOverride ?? paddingLeftOverride ?? 40
      ),
      date: dates[clampedIndex],
      rotate
    });
  }

  if (ticks[ticks.length - 1]?.date.getTime() !== dates[dates.length - 1].getTime()) {
    ticks.push({
      x: clampAxisLabelX(
        coords[coords.length - 1].x,
        coords,
        paddingLeftOverride ?? 40,
        paddingRightOverride ?? paddingLeftOverride ?? 40
      ),
      date: dates[dates.length - 1],
      rotate
    });
  }

  return ticks;
}
